/**
 * What webmail and the console say when a request fails without a reason of
 * the server's own: a gateway's HTML error page (the BFF restarting during an
 * update), a dropped connection. Never a parser's "Unexpected token '<'" or the
 * browser's "Failed to fetch".
 */

/** The body as JSON, or undefined when it is empty or not JSON (an HTML error page). */
export function parseJson(text: string): unknown {
  if (!text) {
    return undefined;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/** A failure the server did not explain, in words, for `service` ("mail service"). */
export function plainFailure(service: string, status: number): string {
  if (status === 502 || status === 503 || status === 504) {
    return `The ${service} is not answering just now; it may be restarting. Try again in a minute.`;
  }
  if (status === 413) {
    return 'That is too large to send.';
  }
  if (status === 429) {
    return 'Too many requests at once. Wait a moment, then try again.';
  }
  if (status >= 500) {
    return `The ${service} ran into a problem (${status}). Try again; if it keeps happening, tell your administrator.`;
  }
  return `The ${service} refused that (${status}).`;
}

/** `fetch`, with a dropped connection said plainly. An abort stays an abort. */
export async function fetchOrExplain(
  input: string,
  init: RequestInit,
  service: string,
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw new Error(`Cannot reach the ${service}. Check your connection, then try again.`);
  }
}
